import React, { useState, useEffect } from 'react';
import {
  PropertyWithDetails,
  PropertyFormData,
  PropertyAddressFormRow,
  PropertyFeeFormRow,
  CreatePropertyRequest,
  UpdatePropertyRequest
} from './types';
import { FormErrors } from '../../../types';
import { useUnsavedChanges } from '../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../hooks/useDirtyChangeReport';
import { useProperties } from './hooks/useProperties';
import { validatePropertyName, validatePropertyDescription } from '../../../utils/validation';
import PropertyInfoSection from './PropertyInfoSection';
import PropertyAddressSection from './PropertyAddressSection';
import PropertyFeesSection from './PropertyFeesSection';
import ContactSearchSection from '../contacts/ContactSearchSection';
import '../../../components/common/CRMForm.css';

interface PropertyFormProps {
  property: PropertyWithDetails | null;
  onSave: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const associationToFormRow = (pa: PropertyWithDetails['propertiesAddresses'][0]): PropertyAddressFormRow => ({
  id: pa.id,
  addressId: pa.addressId,
  categoryId: pa.categoryId,
  isPrimary: pa.isPrimary,
  street1: pa.address.street1,
  street2: pa.address.street2,
  unit: pa.address.unit,
  city: pa.address.city,
  state: pa.address.state,
  zip: pa.address.zip,
  country: pa.address.country
});

const feeToFormRow = (fee: PropertyWithDetails['propertiesFees'][0]): PropertyFeeFormRow => ({
  id: fee.id,
  typeId: fee.typeId,
  description: fee.description,
  amount: fee.amount,
  contactId: fee.contactId,
  interval: fee.interval,
  feeDate: fee.feeDate,
  typeName: fee.typeName,
  contactFirstName: fee.contactFirstName,
  contactLastName: fee.contactLastName,
  contactBusinessName: fee.contactBusinessName
});

const emptyFormData = (): PropertyFormData => ({
  name: '',
  description: '',
  propertiesAddresses: [],
  propertiesFees: [],
  contactIds: []
});

const PropertyForm: React.FC<PropertyFormProps> = ({ property, onSave, onCancel, onDirtyChange }) => {
  const isEditing = !!property;
  const { createProperty, updateProperty } = useProperties();

  const [formData, setFormData] = useState<PropertyFormData>(emptyFormData());
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    addresses: !isEditing,
    fees: !isEditing,
    contacts: !isEditing
  });

  useEffect(() => {
    if (property) {
      setFormData({
        name: property.name,
        description: property.description,
        propertiesAddresses: (property.propertiesAddresses || []).map(associationToFormRow),
        propertiesFees: (property.propertiesFees || []).map(feeToFormRow),
        contactIds: property.contacts.map(contact => contact.id)
      });
      setExpandedSections({ addresses: false, fees: false, contacts: false });
    } else {
      setFormData({
        name: '',
        description: '',
        propertiesAddresses: [{
          id: 0,
          addressId: 0,
          categoryId: 0,
          isPrimary: true
        }],
        propertiesFees: [],
        contactIds: []
      });
      setExpandedSections({ addresses: true, fees: true, contacts: true });
    }
    setErrors({});
    setHasChanges(false);
  }, [property, isEditing]);

  useEffect(() => {
    if (isEditing && property) {
      const savedRows = (property.propertiesAddresses || []).map(associationToFormRow);
      const savedFees = (property.propertiesFees || []).map(feeToFormRow);
      const hasPropertyChanges =
        formData.name !== property.name ||
        formData.description !== property.description ||
        JSON.stringify(formData.propertiesAddresses) !== JSON.stringify(savedRows) ||
        JSON.stringify(formData.propertiesFees) !== JSON.stringify(savedFees);
      const hasContactChanges = JSON.stringify([...formData.contactIds].sort()) !==
        JSON.stringify(property.contacts.map(c => c.id).sort());
      setHasChanges(hasPropertyChanges || hasContactChanges);
    } else if (!isEditing) {
      const hasAnyData =
        formData.name.trim() !== '' ||
        formData.description.trim() !== '' ||
        formData.propertiesAddresses.some(r => r.addressId > 0 || r.categoryId > 0) ||
        formData.propertiesFees.length > 0 ||
        formData.contactIds.length > 0;
      setHasChanges(hasAnyData);
    }
  }, [formData, property, isEditing]);

  useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    const nameError = validatePropertyName(formData.name);
    if (nameError) newErrors.name = nameError;

    const descriptionError = validatePropertyDescription(formData.description);
    if (descriptionError) newErrors.description = descriptionError;

    if (formData.propertiesAddresses.length === 0) {
      newErrors.propertiesAddresses = 'At least one address is required';
    } else {
      const addressErrors: string[] = [];
      formData.propertiesAddresses.forEach((row, index) => {
        if (row.categoryId === 0) {
          addressErrors.push(`Address ${index + 1}: property category is required`);
        }
        if (!row.addressId || row.addressId === 0) {
          addressErrors.push(`Address ${index + 1}: please select an address`);
        }
      });
      if (!formData.propertiesAddresses.some(r => r.isPrimary)) {
        addressErrors.push('Please mark one address as primary');
      }
      const addressIds = formData.propertiesAddresses.map(r => r.addressId).filter(id => id > 0);
      if (new Set(addressIds).size !== addressIds.length) {
        addressErrors.push('Duplicate addresses are not allowed on the same property');
      }
      if (addressErrors.length > 0) {
        newErrors.propertiesAddresses = addressErrors.join(', ');
      }
    }

    if (formData.propertiesFees.length > 0) {
      const feeErrors: string[] = [];
      formData.propertiesFees.forEach((row, index) => {
        if (!row.typeId || row.typeId === 0) {
          feeErrors.push(`Fee ${index + 1}: fee type is required`);
        }
        if (!row.description.trim()) {
          feeErrors.push(`Fee ${index + 1}: fee name is required`);
        } else if (row.description.trim().length > 40) {
          feeErrors.push(`Fee ${index + 1}: fee name must be 40 characters or less`);
        }
        if (!row.amount || row.amount < 0.01) {
          feeErrors.push(`Fee ${index + 1}: amount must be at least $0.01`);
        }
        if (!row.contactId || row.contactId === 0) {
          feeErrors.push(`Fee ${index + 1}: please select a contact`);
        }
        if (!row.interval) {
          feeErrors.push(`Fee ${index + 1}: recurrence interval is required`);
        }
        if (!row.feeDate || !/^\d{2}\/\d{2}\/\d{4}$/.test(row.feeDate)) {
          feeErrors.push(`Fee ${index + 1}: fee date is required (mm/dd/yyyy)`);
        }
      });
      if (feeErrors.length > 0) {
        newErrors.propertiesFees = feeErrors.join(', ');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: 'name' | 'description', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const buildAddressPayload = () =>
    formData.propertiesAddresses.map(row => ({
      ...(row.id > 0 ? { id: row.id } : {}),
      addressId: row.addressId,
      categoryId: row.categoryId,
      isPrimary: row.isPrimary
    }));

  const buildFeesPayload = () =>
    formData.propertiesFees.map(row => ({
      ...(row.id > 0 ? { id: row.id } : {}),
      typeId: row.typeId,
      description: row.description,
      amount: row.amount,
      contactId: row.contactId,
      interval: row.interval,
      feeDate: row.feeDate
    }));

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isEditing && property) {
        const updateData: UpdatePropertyRequest = {
          id: property.id,
          name: formData.name,
          description: formData.description,
          propertiesAddresses: buildAddressPayload(),
          propertiesFees: buildFeesPayload(),
          contactIds: formData.contactIds
        };
        await updateProperty(property.id, updateData);
      } else {
        const createData: CreatePropertyRequest = {
          name: formData.name,
          description: formData.description,
          propertiesAddresses: buildAddressPayload(),
          propertiesFees: buildFeesPayload(),
          contactIds: formData.contactIds
        };
        await createProperty(createData);
      }

      setHasChanges(false);
      onSave();
    } catch (error: unknown) {
      console.error('Error saving property:', error);
      const err = error as { response?: { data?: { error?: string } }; message?: string };
      const message = err.response?.data?.error || err.message || 'Failed to save property';
      setErrors(prev => ({ ...prev, general: message }));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    onCancel();
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="form">
      <div className="form-header">
        <h2>{isEditing ? 'Edit Property' : 'Create Property'}</h2>
        {hasChanges && <span className="form-unsaved-indicator">• Unsaved changes</span>}
      </div>

      {errors.general && (
        <div className="app-error-message">{errors.general}</div>
      )}

      <div className="form-content">
        <section className="form-section">
          <h3>Property Information</h3>
          <PropertyInfoSection
            propertyData={formData}
            errors={errors}
            onChange={handleInputChange}
          />
        </section>

        <section className="form-section">
          <div
            className="form-section-header"
            onClick={() => toggleSection('addresses')}
          >
            <h3>Addresses ({formData.propertiesAddresses.length})</h3>
            <span className={`form-section-toggle ${expandedSections.addresses ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>

          {errors.propertiesAddresses && (
            <div className="app-form-error">{errors.propertiesAddresses}</div>
          )}

          {expandedSections.addresses && (
            <PropertyAddressSection
              propertiesAddresses={formData.propertiesAddresses}
              onChange={(propertiesAddresses) => {
                setFormData(prev => ({ ...prev, propertiesAddresses }));
                setHasChanges(true);
              }}
            />
          )}
        </section>

        <section className="form-section">
          <div
            className="form-section-header"
            onClick={() => toggleSection('fees')}
          >
            <h3>Fees ({formData.propertiesFees.length})</h3>
            <span className={`form-section-toggle ${expandedSections.fees ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>

          {errors.propertiesFees && (
            <div className="app-form-error">{errors.propertiesFees}</div>
          )}

          {expandedSections.fees && (
            <PropertyFeesSection
              propertiesFees={formData.propertiesFees}
              onChange={(propertiesFees) => {
                setFormData(prev => ({ ...prev, propertiesFees }));
                setHasChanges(true);
              }}
            />
          )}
        </section>

        <section className="form-section">
          <div
            className="form-section-header"
            onClick={() => toggleSection('contacts')}
          >
            <h3>Contacts ({formData.contactIds.length})</h3>
            <span className={`form-section-toggle ${expandedSections.contacts ? 'expanded' : ''}`}>
              ▼
            </span>
          </div>

          {expandedSections.contacts && (
            <div className="form-section-content">
              <ContactSearchSection
                selectedContactIds={formData.contactIds}
                onContactsChange={(contactIds) => setFormData(prev => ({ ...prev, contactIds }))}
                label="Search Contacts"
                placeholder="Search contacts by first name or last name..."
              />
            </div>
          )}
        </section>
      </div>

      <div className="app-form-actions">
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={handleCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="app-btn app-btn-primary"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : (isEditing ? 'Update Property' : 'Create Property')}
        </button>
      </div>
    </div>
  );
};

export default PropertyForm;
